import * as React from 'react';
import { toast } from 'sonner';
import {
  isActiveReportExport,
  isReadyReportExport,
  reportExportErrorMessage,
  useReportExportAvailabilityStore,
  useCancelReportExport,
  useCreateReportExport,
  useReportExports,
} from '@discovr/features';
import type { CreatedReportExport, ReportExportOperation } from '@discovr/features';
import type { ReportExportJob } from '@discovr/types';
import { Button } from '../components/ui/button';
import { Download, Loader2 } from '../icons';
import { isExportOwner, useCurrentUser } from './exportOwnership';
import { ExportStaleHint } from './ExportStaleHint';
import { awaitReportExportDownload, downloadReportExport, logExportFailure, shownTerminalEvent } from './reportExportNotifications';
import { requesterName } from './reportExportView';
import { useExportAnnouncement } from './useExportAnnouncement';

const EXPORT_LABEL = 'Export to Excel';
export const EXPORT_UNAVAILABLE_LABEL = 'Export is temporarily unavailable';
const PREPARING_LABEL = 'Preparing…';
const CANCELLING_LABEL = 'Cancelling…';
const PREPARING_TOAST = 'Preparing report...';
const DOWNLOADING_TOAST = 'Downloading report...';
const ICON_SIZE = 16;

interface ReportExportButtonProps {
  campaignId: string;
}

function failureHandler(operation: ReportExportOperation) {
  return (err: Error): void => {
    logExportFailure(err);
    toast.error(reportExportErrorMessage(err, operation));
  };
}

/** A stored export from the last day comes back already done and downloads at once; a new or
 *  running one downloads in this tab when its ready event arrives (reportExportNotifications). */
function handleCreated({ job, joined }: CreatedReportExport): void {
  const shown = shownTerminalEvent(job.id);
  if (isReadyReportExport(job) || shown === 'report_export.ready') {
    toast.info(DOWNLOADING_TOAST, { id: job.id });
    void downloadReportExport(job.campaign_id, job.id, job.id);
    return;
  }
  if (shown) return;
  awaitReportExportDownload(job.id);
  toast.info(joined ? `Joined the export started by ${requesterName(job)}` : PREPARING_TOAST, { id: job.id });
}

function exportLabel(isBusy: boolean, isCancelling: boolean, othersJob: ReportExportJob | undefined): string {
  if (isCancelling) return CANCELLING_LABEL;
  if (othersJob) return `Export in progress (by ${requesterName(othersJob)})`;
  return isBusy ? PREPARING_LABEL : EXPORT_LABEL;
}

/** Downloads the Reporting page as Excel (admin app only): the stored file when one was built in
 *  the last day, else a new async export that downloads once ready. One campaign has one active
 *  export: the button stays disabled until it ends. */
export function ReportExportButton({ campaignId }: ReportExportButtonProps) {
  const exportButtonRef = React.useRef<HTMLButtonElement>(null);
  const { data: jobs, isError, refetch } = useReportExports(campaignId);
  const isUnavailable = useReportExportAvailabilityStore((s) => s.unavailableCampaignIds.has(campaignId));
  const announcement = useExportAnnouncement(jobs);
  const activeJob = jobs?.find(isActiveReportExport);
  const { userId, isLoading: isProfileLoading } = useCurrentUser();
  const isOwnJob = activeJob !== undefined && isExportOwner(activeJob, userId);
  const { createExport, isCreating } = useCreateReportExport(campaignId, {
    onSuccess: handleCreated,
    onError: failureHandler('create'),
  });
  const { cancelExport, isCancelling: isCancelSending } = useCancelReportExport(campaignId, { onError: failureHandler('cancel') });

  const othersJob = activeJob && !isOwnJob && !isProfileLoading && !isCreating ? activeJob : undefined;
  const isBusy = isCreating || activeJob !== undefined;
  const isCancelling = isCancelSending || activeJob?.cancel_requested === true;
  if (isUnavailable) {
    return (
      <Button variant="outline" className="rounded-full aria-disabled:pointer-events-none aria-disabled:opacity-50" aria-disabled>
        <Download size={ICON_SIZE} className="size-4" aria-hidden />
        {EXPORT_UNAVAILABLE_LABEL}
      </Button>
    );
  }
  const icon = isBusy
    ? <Loader2 size={ICON_SIZE} className="size-4 animate-spin" aria-hidden />
    : <Download size={ICON_SIZE} className="size-4" aria-hidden />;

  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        ref={exportButtonRef}
        variant="outline"
        className="rounded-full aria-disabled:pointer-events-none aria-disabled:opacity-50"
        onClick={() => {
          if (!isBusy) createExport();
        }}
        aria-disabled={isBusy}
        aria-busy={isBusy}
      >
        {icon}
        {exportLabel(isBusy, isCancelling, othersJob)}
      </Button>
      <span role="status" aria-live="polite" className="sr-only">{announcement}</span>
      {isError && jobs !== undefined && <ExportStaleHint onRetry={() => void refetch()} />}
      {activeJob && isOwnJob && !isCancelling && (
        <Button variant="ghost" className="rounded-full" onClick={() => {
            cancelExport(activeJob.id);
            exportButtonRef.current?.focus();
          }}>
          Cancel
        </Button>
      )}
    </div>
  );
}
